/**
 * interjectClient — POST helpers for the human-in-the-loop steering channel.
 *
 * Backend: `POST /api/debate/{sessionId}/interject` with `{ text }`.
 * The engine echoes the message via SSE (`user.interject`) so the FE
 * store updates from the same source-of-truth as the agents.
 */
import { withCsrfHeader } from "./csrf";

export interface InterjectResponse {
  ok: true;
  sessionId: string;
  id: string;
  receivedAt: number;
  queuePosition?: number;
}

export class InterjectError extends Error {
  override readonly name = "InterjectError";
  constructor(
    message: string,
    public readonly status?: number,
    public readonly code?: string,
  ) {
    super(message);
    Object.setPrototypeOf(this, InterjectError.prototype);
  }
}

export async function submitInterjection(
  sessionId: string,
  text: string,
  init?: { signal?: AbortSignal },
): Promise<InterjectResponse> {
  const trimmed = text.trim();
  if (!trimmed) throw new InterjectError("empty interjection text");
  if (trimmed.length > 2000) {
    throw new InterjectError("interjection exceeds 2000 characters");
  }

  const reqInit: RequestInit = {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: withCsrfHeader({
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify({ text: trimmed }),
  };
  if (init?.signal) reqInit.signal = init.signal;
  const res = await fetch(
    `/api/debate/${encodeURIComponent(sessionId)}/interject`,
    reqInit,
  );

  if (!res.ok) {
    let code: string | undefined;
    let message = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { code?: string; message?: string };
      code = body.code;
      message = body.message ?? message;
    } catch {
      /* keep default */
    }
    throw new InterjectError(message, res.status, code);
  }

  return (await res.json()) as InterjectResponse;
}
